import {gql} from '@apollo/client';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getPowAgnosticPublishedFlowchartVersionFromProcedure,
  getPowAgnosticPublishedFlowchartVersionId,
} from 'procedures/utils/powAgnosticFlowchartHelpers';

import {
  EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT,
  ExperimentFlowchart,
} from '../design-state/ExperimentalInputs';
import type {
  ProcedureVersionUpdateCheckQuery,
  ProcedureVersionUpdateCheckQueryVariables,
} from './__generated/ProcedureVersionUpdateCheckQuery';

export const PROCEDURE_VERSION_UPDATE_CHECK_QUERY = gql`
  query ProcedureVersionUpdateCheckQuery($procedureId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedure(id: $procedureId) {
      ... on ProcedureNode {
        id
        procedureFlowchart {
          ... on ProcedureFlowchartNode {
            id
            publishedFlowchartVersion {
              ...ExperimentalInputsFlowchartGraphQL
            }
          }
        }
        publishedProcedureFlowchartConfigVersion {
          ...ProcedureFlowchartConfigVersionExperimentalInputsGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT}
  ${getFragmentWrapper('ProcedureFlowchartConfigVersionExperimentalInputs').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ProcedureVersionUpdateCheckResult {
  isLoading: boolean;
  hasNewerVersion: boolean;
  latestPublishedVersion: ExperimentFlowchart | null;
  isPoW: boolean;
}

/**
 * Hook to check if a procedure has a newer published version than the one used by a procedure run plan.
 *
 * @param procedureId - The ID of the procedure to check
 * @param currentVersionIdInPlan - The version ID currently used by the plan (either template or PoW version)
 * @returns Object containing:
 *   - isLoading: Whether the query is still loading
 *   - hasNewerVersion: True if the procedure has a newer published version
 *   - latestPublishedVersion: The full published version object (for input preservation)
 *   - isPoW: Whether the latest version is a PoW procedure
 */
export function useProcedureVersionUpdateCheck(
  procedureId: string | null,
  currentVersionIdInPlan: string | null
): ProcedureVersionUpdateCheckResult {
  const result = useSkippableBenchlingQuery<
    ProcedureVersionUpdateCheckQuery,
    ProcedureVersionUpdateCheckQueryVariables
  >(PROCEDURE_VERSION_UPDATE_CHECK_QUERY, {
    skip: procedureId == null,
    variables: {procedureId: procedureId!},
    fetchPolicy: 'network-only',
  });

  if (result.type === QueryResultType.PENDING) {
    return {
      isLoading: true,
      hasNewerVersion: false,
      latestPublishedVersion: null,
      isPoW: false,
    };
  }

  if (result.type === QueryResultType.ERROR || result.data == null) {
    return {
      isLoading: false,
      hasNewerVersion: false,
      latestPublishedVersion: null,
      isPoW: false,
    };
  }

  const {procedure} = result.data;
  if (isInaccessible(procedure)) {
    return {
      isLoading: false,
      hasNewerVersion: false,
      latestPublishedVersion: null,
      isPoW: false,
    };
  }

  const latestPublishedVersionId = getPowAgnosticPublishedFlowchartVersionId(procedure);
  const latestPublishedVersion = getPowAgnosticPublishedFlowchartVersionFromProcedure(procedure);
  const isPoW = procedure.publishedProcedureFlowchartConfigVersion != null;

  if (
    latestPublishedVersionId == null ||
    latestPublishedVersion == null ||
    isInaccessible(latestPublishedVersion)
  ) {
    return {
      isLoading: false,
      hasNewerVersion: false,
      latestPublishedVersion: null,
      isPoW,
    };
  }

  const hasNewerVersion =
    currentVersionIdInPlan != null && latestPublishedVersionId !== currentVersionIdInPlan;

  return {
    isLoading: false,
    hasNewerVersion,
    latestPublishedVersion: {
      id: latestPublishedVersion.id,
      nodes: [...latestPublishedVersion.nodes],
      edges: [...latestPublishedVersion.edges],
    },
    isPoW,
  };
}
